/**
 * **When a group of photographs is allowed to appear** — the second half of the loading policy,
 * for the one kind of content the first half never covered.
 *
 * Owner, 17 September 2026, on the plans list: the covers arrive one at a time, and the list reads
 * as something assembling itself rather than as something that loaded. What he asked for is the
 * grey box first and then the pictures **together**.
 *
 * ── Why `loadingGate` could not answer this on its own ───────────────────────────────────────
 * That gate turns "the reads are open" into "draw the skeleton", and it works because a query
 * either has answered or has not. A photograph is not a read this app makes: the browser fetches it
 * from the markup, on its own schedule, and — with `loading="lazy"` on a list that can run past a
 * screenful — a cover far down the page may never be fetched at all. There is no moment at which
 * "all of them have landed" is a true sentence, so a gate that waits for one would wait forever and
 * a ceiling on that wait would hold the visible covers grey for the length of the ceiling.
 *
 * ── So the window opens on the FIRST arrival, not on the last ────────────────────────────────
 * The covers a trainer is actually looking at are requested in the same frame, from the same
 * server, and land within a few tens of milliseconds of each other. So the first one to land opens
 * a short window, everything that lands inside it is held, and when the window closes they all
 * appear at once. A straggler — or a cover that is only fetched because the trainer scrolled to it
 * — arrives after the window has closed and simply paints. That is the right outcome rather than a
 * concession: nobody is watching a list assemble at the moment they are scrolling it.
 *
 * ── It defers INK, never a box, and that is what separates it from a masking timer ───────────
 * The loading policy bans a `setTimeout` that delays content so a measurement cannot see it move.
 * The distinction is the same one `loadingGate` makes: every photo box in this app declares its own
 * width and height (`shared/ui/photoBox`), so the box is on screen at its final size throughout,
 * the grey ground is showing in it, and nothing this decision switches can change a single
 * coordinate. What is held is the picture, in a hole that is already the picture's size.
 *
 * Pure: the clock arrives as a parameter and the verdict says when to look again. No React, no DOM.
 */

/**
 * How long the group holds its arrivals before showing them.
 *
 * The same 150ms `SKELETON_GRACE_MS` spends, and for a related reason — it is about the shortest
 * span in which a person reads two events as one. Long enough to collect the covers of a screenful
 * off one connection, short enough that a trainer who is waiting never learns it is there.
 */
export const PHOTO_COALESCE_MS = 150;

export interface PhotoRevealInput {
  /** How many photographs this group draws at all. Zero means there is nothing to hold. */
  readonly photoCount: number;
  /** When the group's first photograph landed or failed, or `null` while none has. */
  readonly firstSettledAt: number | null;
  readonly now: number;
}

export interface PhotoRevealVerdict {
  /** Whether the group's photographs belong on screen. */
  readonly revealed: boolean;
  /** When this answer changes without new input, or `null` when nothing is pending on the clock. */
  readonly recheckAt: number | null;
}

/**
 * The whole decision, for one group, at one instant.
 *
 * A group with no photographs is revealed immediately: there is no wait, so a caller that asks
 * about an empty list is never handed the holding class and cannot hide something it will not be
 * told to show. A group whose photographs have all failed still settles — a failure is an arrival,
 * and `photoBox` documents the ground as the error state.
 */
export function photoRevealGate(input: PhotoRevealInput): PhotoRevealVerdict {
  const { photoCount, firstSettledAt, now } = input;

  if (photoCount === 0) return { revealed: true, recheckAt: null };
  if (firstSettledAt === null) return { revealed: false, recheckAt: null };

  const openUntil = firstSettledAt + PHOTO_COALESCE_MS;
  if (now < openUntil) return { revealed: false, recheckAt: openUntil };
  return { revealed: true, recheckAt: null };
}
